import type { TConversationThreadMessageDTO } from '@conversed/rest/src/contracts/train-ai/train-ai.dto';
import { getHotTopicDisplayName } from '@helpers/hotTopicHelper';
import type React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  countFittingChips,
  getDistinctThreadTopics,
} from './conversation-thread.helpers';

type Props = {
  messages: TConversationThreadMessageDTO[];
  isLoading: boolean;
};

const STAT_LABEL_CLASS =
  'text-pico font-medium uppercase tracking-wider text-gray-400';

const TOPIC_CHIP_CLASS =
  'min-w-0 truncate rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-timestamp font-medium text-purple-600';

const MORE_BUTTON_CLASS =
  'shrink-0 whitespace-nowrap rounded-full border border-gray-200 bg-white px-2 py-0.5 text-timestamp font-medium text-gray-500 hover:text-gray-800';

/**
 * Summary row above the conversation: how many questions it has and which topics it
 * touched. The topics stay on one line, showing as many as fit, followed by "+N more".
 */
export const ConversationReviewThreadStats: React.FC<Props> = ({
  messages,
  isLoading,
}) => {
  const { t } = useTranslation();
  const topics = getDistinctThreadTopics(messages);
  const [isShowingAllTopics, setIsShowingAllTopics] = useState(false);
  const [fittingTopicCount, setFittingTopicCount] = useState(topics.length);
  const topicRowRef = useRef<HTMLDivElement>(null);
  const measureRowRef = useRef<HTMLDivElement>(null);

  // * Chip widths are read from an invisible copy of the full row, so the count can be
  // * recomputed on every resize without the visible row first showing every chip.
  useLayoutEffect(() => {
    const topicRow = topicRowRef.current;
    const measureRow = measureRowRef.current;

    if (!topicRow || !measureRow) {
      return;
    }

    const updateFittingTopicCount = () => {
      const chipWidths = [
        ...measureRow.querySelectorAll<HTMLElement>('[data-measure="chip"]'),
      ].map((chip) => chip.offsetWidth);
      const moreButton = measureRow.querySelector<HTMLElement>(
        '[data-measure="more"]',
      );

      setFittingTopicCount(
        countFittingChips({
          chipWidths,
          availableWidth: topicRow.clientWidth,
          moreButtonWidth: moreButton?.offsetWidth ?? 0,
          gap: Number.parseFloat(getComputedStyle(measureRow).columnGap) || 0,
        }),
      );
    };

    updateFittingTopicCount();
    const resizeObserver = new ResizeObserver(updateFittingTopicCount);
    resizeObserver.observe(topicRow);

    return () => {
      resizeObserver.disconnect();
    };
  }, [messages]);

  const visibleTopics = isShowingAllTopics
    ? topics
    : topics.slice(0, fittingTopicCount);
  const hiddenTopicCount = topics.length - visibleTopics.length;

  return (
    <div className='flex items-start gap-8'>
      <div className='flex shrink-0 flex-col'>
        <span className={STAT_LABEL_CLASS}>
          {t('train-ai.conversation-review.thread.total-questions')}
        </span>
        <span className='text-3xl font-bold text-gray-800'>
          {isLoading ? '-' : messages.length}
        </span>
      </div>

      <div className='relative flex min-w-0 flex-1 flex-col gap-1'>
        <span className={STAT_LABEL_CLASS}>
          {t('train-ai.conversation-review.thread.topics')}
        </span>
        <div
          ref={topicRowRef}
          className={
            isShowingAllTopics
              ? 'flex flex-wrap gap-1'
              : 'flex flex-nowrap gap-1 overflow-hidden'
          }>
          {visibleTopics.map((topic) => {
            const topicName = getHotTopicDisplayName(topic.name);

            return (
              <span
                key={topic.id}
                title={topicName}
                className={TOPIC_CHIP_CLASS}>
                {topicName}
              </span>
            );
          })}
          {hiddenTopicCount > 0 && (
            <button
              type='button'
              className={MORE_BUTTON_CLASS}
              onClick={() => setIsShowingAllTopics(true)}>
              {t('train-ai.conversation-review.thread.topics-more', {
                count: hiddenTopicCount,
              })}
            </button>
          )}
          {isShowingAllTopics && topics.length > fittingTopicCount && (
            <button
              type='button'
              className='whitespace-nowrap px-1 py-0.5 text-timestamp font-medium text-gray-500 underline decoration-dotted underline-offset-2 hover:text-gray-800'
              onClick={() => setIsShowingAllTopics(false)}>
              {t('train-ai.conversation-review.thread.topics-less')}
            </button>
          )}
        </div>

        <div
          ref={measureRowRef}
          aria-hidden='true'
          className='invisible pointer-events-none absolute left-0 top-0 flex w-max gap-1 whitespace-nowrap'>
          {topics.map((topic) => (
            <span
              key={topic.id}
              data-measure='chip'
              className={TOPIC_CHIP_CLASS}>
              {getHotTopicDisplayName(topic.name)}
            </span>
          ))}
          <span data-measure='more' className={MORE_BUTTON_CLASS}>
            {t('train-ai.conversation-review.thread.topics-more', {
              count: topics.length,
            })}
          </span>
        </div>
      </div>
    </div>
  );
};
